/**
 * CEL hover provider for showing type info and descriptions on hover.
 *
 * When the user hovers over a variable, field, function, or constant,
 * displays a tooltip with the type and description from the schema.
 *
 * @module cel/hoverProvider
 */

import type monaco from 'monaco-editor'

import type { SchemaRegistry } from './types'
import { parseExpressionContext, resolveTypeForPath } from './utils'

/**
 * Create a Monaco hover provider for CEL expressions.
 *
 * The provider analyzes the hovered word and its context to determine
 * whether it's a field access (e.g., `subject.email`) or a top-level
 * identifier (variable, function, or constant), then displays type info.
 *
 * @param schemaRegistry - Registry mapping model URIs to CEL schemas
 * @returns Monaco HoverProvider instance
 *
 * @example
 * ```ts
 * const schemaRegistry = new Map<string, CELSchema>()
 * const hoverProvider = makeCELHoverProvider(schemaRegistry)
 * monaco.languages.registerHoverProvider('cel', hoverProvider)
 *
 * // Hovering over "email" in "subject.email" shows:
 * // **email**: `string`
 * // The user's primary email address
 * ```
 */
export const makeCELHoverProvider = (
    schemaRegistry: SchemaRegistry,
): monaco.languages.HoverProvider => ({
    provideHover: (model, position): monaco.languages.Hover | undefined => {
        const schema = schemaRegistry.get(model.uri.toString())
        if (!schema) return undefined

        const line = model.getLineContent(position.lineNumber)
        const word = model.getWordAtPosition(position)
        if (!word) return undefined

        // Get the word range for the hover
        const wordRange = {
            startLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endLineNumber: position.lineNumber,
            endColumn: word.endColumn,
        }

        // Parse context to understand if this is a field access or top-level
        const { basePath } = parseExpressionContext(line, word.endColumn)
        const hoveredWord = word.word

        let name: string
        let typeName: string | undefined
        let description: string | undefined

        if (basePath.length > 0) {
            // This is a field access like "subject.email" - hovering over "email"
            const parentType = resolveTypeForPath(basePath, schema)
            if (!parentType) return undefined

            const typeDef = schema.types.find((t) => t.name === parentType)
            if (!typeDef) return undefined

            const field = typeDef.fields.find((f) => f.name === hoveredWord)
            if (!field) return undefined

            name = hoveredWord
            typeName = field.typeName
            description = field.description
        } else {
            // Top-level: could be a variable, function, or constant
            const variable = schema.variables.find((v) => v.name === hoveredWord)
            if (variable) {
                name = variable.name
                typeName = variable.typeName
                description = variable.description
            } else {
                const fn = schema.functions.find((f) => f.name === hoveredWord || f.name.endsWith(`.${hoveredWord}`))
                if (fn) {
                    name = fn.name
                    typeName = `(${fn.paramTypes.join(', ')}) → ${fn.returnType}`
                    description = fn.description
                } else {
                    const constant = schema.constants.find((c) => c.name === hoveredWord || c.name.endsWith(`.${hoveredWord}`))
                    if (constant) {
                        name = constant.name
                        typeName = constant.typeName
                        description = constant.description
                    } else {
                        return undefined
                    }
                }
            }
        }

        const contents: monaco.IMarkdownString[] = [
            { value: `**${name}**: \`${typeName}\`` },
        ]
        if (description) {
            contents.push({ value: description })
        }

        return {
            contents,
            range: wordRange,
        }
    },
})
